import { useState, useEffect } from 'react';
import { getClientKey } from '../utils/clientUtils';

const usePinnedListState = (pinnedClients, viewType) => {
  // Sorting state
  const [sortConfig, setSortConfig] = useState({
    key: null,
    direction: 'ascending'
  });
  
  // Search state for filtering pinned clients
  const [pinnedSearchQuery, setPinnedSearchQuery] = useState('');
  
  // Tier filtering state
  const [activeTierFilters, setActiveTierFilters] = useState(new Set());
  
  // Lock state for manually pinning clients to top
  const [lockedClients, setLockedClients] = useState(new Set());
  
  // Card flip state
  const [flippedCards, setFlippedCards] = useState(new Set());

  // Table row expansion state
  const [expandedTableRow, setExpandedTableRow] = useState(null);
  
  // Reset expanded states when view type changes
  useEffect(() => {
    setExpandedTableRow(null);
    setFlippedCards(new Set());
  }, [viewType]);

  // Clear expanded row if the expanded client is no longer in the pinned list
  useEffect(() => {
    if (expandedTableRow) {
      const isStillPinned = pinnedClients.some(client => getClientKey(client) === expandedTableRow);
      if (!isStillPinned) {
        setExpandedTableRow(null);
      }
    }
  }, [pinnedClients, expandedTableRow]);

  // Request sort for a specific column
  const requestSort = (key) => {
    let direction = 'ascending';
    if (sortConfig.key === key && sortConfig.direction === 'ascending') {
      direction = 'descending';
    }
    setSortConfig({ key, direction });
  };
  
  // Get the sort direction indicator for a column
  const getSortDirectionIndicator = (key) => {
    if (sortConfig.key !== key) return ' ⇅'; // Show sortable indicator
    return sortConfig.direction === 'ascending' ? ' ↑' : ' ↓';
  };

  // Lock/unlock client functionality
  const isClientLocked = (client) => {
    return lockedClients.has(getClientKey(client));
  };

  const toggleClientLock = (client) => {
    const clientKey = getClientKey(client);
    const newLockedClients = new Set(lockedClients);
    
    if (newLockedClients.has(clientKey)) {
      newLockedClients.delete(clientKey);
    } else {
      newLockedClients.add(clientKey);
    }
    
    setLockedClients(newLockedClients);
  };

  // Card flip functionality
  const isCardFlipped = (client) => {
    return flippedCards.has(getClientKey(client));
  };

  const toggleCardFlip = (client) => {
    const clientKey = getClientKey(client);
    const newFlippedCards = new Set(flippedCards);
    
    if (newFlippedCards.has(clientKey)) {
      newFlippedCards.delete(clientKey);
    } else {
      newFlippedCards.add(clientKey);
    }
    
    setFlippedCards(newFlippedCards);
  };

  // Table row expansion functionality
  const toggleTableRowExpansion = (client, index) => {
    const clientKey = getClientKey(client);
    if (expandedTableRow === clientKey) {
      setExpandedTableRow(null); // Collapse if already expanded
    } else {
      setExpandedTableRow(clientKey); // Expand this row, collapse others
    }
  };

  // Check if table row is expanded
  const isTableRowExpanded = (client) => {
    return expandedTableRow === getClientKey(client);
  };

  return {
    // State values
    sortConfig,
    pinnedSearchQuery,
    activeTierFilters,
    lockedClients,
    flippedCards,
    expandedTableRow,
    
    // State setters
    setSortConfig,
    setPinnedSearchQuery,
    setActiveTierFilters,
    setLockedClients,
    
    // Helper functions
    requestSort,
    getSortDirectionIndicator,
    isClientLocked,
    toggleClientLock,
    isCardFlipped,
    toggleCardFlip,
    toggleTableRowExpansion,
    isTableRowExpanded
  };
};

export default usePinnedListState;
